// API base URL — production'da otomatik aynı origin'i kullanır
const API_BASE = '/api';
const TOKEN_KEY = 'ygApiToken';

export function getToken(): string {
  return localStorage.getItem(TOKEN_KEY) ?? '';
}

export function setToken(token: string): void {
  localStorage.setItem(TOKEN_KEY, token);
}

export function clearToken(): void {
  localStorage.removeItem(TOKEN_KEY);
}

async function request<T>(
  path: string,
  options: Parameters<typeof fetch>[1] = {}
): Promise<T> {
  const token = getToken();
  const devAuth = localStorage.getItem('yg_dev_auth'); // Dev Panel şifre/token
  
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string> ?? {}),
  };
  
  if (token) headers['X-Admin-Token'] = token;
  // Eğer geliştirici paneli şifresi biliniyorsa bypass başlığı ekle
  if (devAuth) {
    try {
      const parsed = JSON.parse(devAuth);
      if (parsed.password) headers['X-Dev-Key'] = parsed.password;
    } catch { /* sessizce */ }
  }

  const res = await fetch(`${API_BASE}${path}`, { ...options, headers });

  // Boş yanıt kontrolü
  const text = await res.text();
  if (!text || text.trim() === '') {
    throw new Error(`Sunucu boş yanıt döndürdü (HTTP ${res.status})`);
  }

  let json: { success: boolean; message?: string; data?: unknown };
  try {
    json = JSON.parse(text);
  } catch {
    // PHP hata mesajı veya HTML içeren yanıt — Daha detaylı hata göster
    const preview = text.substring(0, 500);
    console.error('API Parse Hatası:', text);
    throw new Error(`Sunucu yanıtı okunamadı (Geçersiz JSON). Hata muhtemelen PHP tarafında. İlk 500 karakter: ${preview}`);
  }

  if (!json.success) throw new Error(json.message ?? 'API hatası');
  return json.data as T;
}

// ── Site Data ────────────────────────────────────────────────────────────────

export async function fetchAllSiteData(): Promise<Record<string, unknown>> {
  return request<Record<string, unknown>>('/site-data.php');
}

export async function saveSiteData(key: string, value: unknown): Promise<void> {
  await request('/site-data.php', {
    method: 'POST',
    body: JSON.stringify({ key, value }),
  });
}

// ── Auth ─────────────────────────────────────────────────────────────────────

export async function apiLogin(username: string, password: string) {
  return request<{ token: string; user: ApiUser }>('/auth.php', {
    method: 'POST',
    body: JSON.stringify({ action: 'login', username, password }),
  });
}

export async function apiLogout(): Promise<void> {
  await request('/auth.php', {
    method: 'POST',
    body: JSON.stringify({ action: 'logout' }),
  });
}

export async function apiGetUsers(): Promise<ApiUser[]> {
  return request<ApiUser[]>('/auth.php', {
    method: 'POST',
    body: JSON.stringify({ action: 'getUsers' }),
  });
}

export async function apiAddUser(data: {
  username: string;
  displayName: string;
  password: string;
  role: 'admin' | 'editor' | 'support';
}): Promise<{ id: string }> {
  return request<{ id: string }>('/auth.php', {
    method: 'POST',
    body: JSON.stringify({ action: 'addUser', ...data }),
  });
}

export async function apiUpdateUser(id: string, updates: Partial<ApiUser & { password: string }>): Promise<void> {
  await request('/auth.php', {
    method: 'POST',
    body: JSON.stringify({ action: 'updateUser', id, updates }),
  });
}

export async function apiDeleteUser(id: string): Promise<void> {
  await request('/auth.php', {
    method: 'POST',
    body: JSON.stringify({ action: 'deleteUser', id }),
  });
}

// ── Upload ───────────────────────────────────────────────────────────────────

export async function uploadFile(file: File): Promise<{ id: string; url: string }> {
  const token = getToken();
  const formData = new FormData();
  formData.append('file', file);
  const res = await fetch(`${API_BASE}/upload.php`, {
    method: 'POST',
    headers: token ? { 'X-Admin-Token': token } : {},
    body: formData,
  });
  const json = await res.json();
  if (!json.success) throw new Error(json.message ?? 'Yükleme hatası');
  return json.data as { id: string; url: string };
}

export async function deleteUploadedFile(id: string): Promise<void> {
  await request(`/upload.php?id=${encodeURIComponent(id)}`, { method: 'DELETE' });
}

// ── Messages ──────────────────────────────────────────────────────────────────

export interface ApiMessage {
  id: number;
  name: string;
  email: string;
  phone: string;
  subject: string;
  message: string;
  project: string;
  status: 'new' | 'read' | 'replied';
  date: string;
  createdAt: string;
}

export async function submitContactForm(data: {
  ad_soyad: string;
  email: string;
  telefon: string;
  konu: string;
  mesaj: string;
}): Promise<{ id: number }> {
  const res = await fetch('/api/messages.php', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ action: 'submit', ...data }),
  });
  const text = await res.text();
  if (!text || text.trim() === '') throw new Error('Sunucu boş yanıt döndürdü');
  let json: { success: boolean; message?: string; data?: unknown };
  try { json = JSON.parse(text); } catch { throw new Error('Geçersiz sunucu yanıtı'); }
  if (!json.success) throw new Error(json.message ?? 'Gönderim hatası');
  return json.data as { id: number };
}

export async function apiGetMessages(status?: string): Promise<ApiMessage[]> {
  const url = status ? `/messages.php?status=${status}` : '/messages.php';
  return request<ApiMessage[]>(url);
}

export async function apiUpdateMessageStatus(id: number, status: 'new' | 'read' | 'replied'): Promise<void> {
  await request('/messages.php', {
    method: 'POST',
    body: JSON.stringify({ action: 'updateStatus', id, status }),
  });
}

export async function apiDeleteMessage(id: number): Promise<void> {
  await request('/messages.php', {
    method: 'POST',
    body: JSON.stringify({ action: 'delete', id }),
  });
}

// ── Alias for backward compat ────────────────────────────────────────────────

// ── Types ─────────────────────────────────────────────────────────────────────

export interface ApiUser {
  id: string;
  username: string;
  displayName: string;
  role: 'admin' | 'editor' | 'support';
  createdAt: string;
  lastLogin?: string;
}
